import { useQuery, type QueryClient } from "@tanstack/react-query";
import { sendAjaxRequest }from "@/ts/api/comunicacion";
import z from "zod";
import type { Respuesta } from "@/ts/typings/clases";

export function useOrdenes(parametros: UseOrdenesParams) {
	return useQuery({
		queryKey: ["getOFsEnCurso", parametros],
		queryFn: () => {
			return sendAjaxRequest<InfoOF[]>("GET", "/api/v1/getOFsEnCurso", parametros);
		},
	});
}
export async function updateCacheOF(idOF: number, queryClient: QueryClient) {
	const newOF = await sendAjaxRequest<InfoOF>("GET", "/api/v1/getOF", { idOF });

	queryClient.setQueriesData({ queryKey: ["getOFsEnCurso"] }, (oldData: InfoOF[] | undefined) => {
		if (!oldData) return oldData;

		return oldData.map(of => {
			if (of.idOF === idOF) return newOF;
			return of;
		});
	});
}
export interface UseOrdenesParams {
	idsFamilia?: string[];
	idsSubFamilia?: string[];
	idMaquina?: string;
};
export interface InfoOF {
	idOF: number;
	idMaquina: number;
	idConjunto: string;
	descripcion: string;
	cantLanzada: number;
	cantAcabada: number;
	fases: InfoFase[];
}

export interface InfoFase {
	idOF: number;
	idFase: number;
	descripcion: string;
	idPuesto: string;

	cantAcabada: number;
	cantLanzada: number;
	cantRechazada: number;

	timestampFin: number | null;
	timestampIni: number | null;

	tiempoEjecucionMano: number | null;
	tiempoEjecucionMaq: number | null;
}


export async function lanzarOF(props: { idConjunto: string, values: iInputsCrearOF, componentes: InfoComponente[], sendFases: boolean, textoOF: string; }) {
	const { idConjunto, values, componentes, sendFases, textoOF } = props;

	const parametro = {
		cantidad: values.cantidadOrden,
		idConjunto: idConjunto,
		idProveedor: values.selectorProveedor,
		prioridad: values.inputPrioridad,
		idOPE: values.selectorOPE,
		textoOF: textoOF,
		componentes: componentes.map(comp => ({
			id: comp.idComponente,
			cantidadPorArticulo: comp.cantidadPorArticulo,
		})),
		mapeoFases: sendFases ? componentes.map(comp => ({
			idFase: comp.faseUsado,
			componenteId: comp.idComponente,
			componenteFase: 0,
		})) : null,
	};

	const url = "/api/v1/anadirOF";
	return sendAjaxRequest<Respuesta>("POST", url, parametro);

}
export async function lanzarOPE(idConjunto: string, values: iInputsCrearOF) {
	const parametro = {
		idConjunto: idConjunto,

		idOPE: values.selectorOPE,
		idProveedor: values.selectorProveedor,

		lote: values.inputLote,
		cantidad: values.cantidadOrden,
	};

	if (parametro.cantidad == null || isNaN(parametro.cantidad)) {
		return { tipo: "error", message: "La cantidad no es un número válido" };
	}
	if (parametro.idProveedor == "" || parametro.idProveedor == null) {
		return { tipo: "error", message: "Debe asignar un proveedor a OF con OPE" };
	}
	if (parametro.idOPE == "" || parametro.idOPE == null) {
		return { tipo: "error", message: "Debe asignar una id de OPE" };
	}

	const url = "/api/v1/anadirOPE";
	return sendAjaxRequest<Respuesta>("POST", url, parametro);


}



export function useDatosFabricacion(idArticulo: string | null) {
	return useQuery<Conjunto | null>({
		queryKey: ["getDatosFabricacionArticulo", idArticulo],
		queryFn: () => {
			if (idArticulo == null) return Promise.resolve(null);
			return sendAjaxRequest<Conjunto>("GET", "/api/v1/getDatosFabricacionArticulo", { "idconjunto": idArticulo, });
		}
	});
}
export function useOPEsCompatibles(idProveedor: string | null) {
	return useQuery<OPE[]>({
		queryKey: ["getOPEs", idProveedor],
		queryFn: () => {
			if (idProveedor == null) return Promise.resolve([]);
			return sendAjaxRequest<OPE[]>("GET", "/api/v1/getOPEs", { idProveedor });
		}
	});
}

export function getArticuloByReference(referenciaArticulo: string): Promise<InfoArticulo> {
	const parametro = {
		"referencia": referenciaArticulo,
	};

	const url = "/api/v1/getDatosArticulo";
	return sendAjaxRequest<InfoArticulo>("GET", url, parametro);
}




export interface Conjunto {
	// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- String seria un error, y quiero el autocompletado
	tipoArticulo: "OF" | "OPE" | "Compras" | "OF+OPE" | string;
	descripcion: string;
	cantidadOptima: number | null;
	defaultProveedor: string | null;
	componentes: InfoComponente[];
	proveedores: Proveedor[];
	ruta: {
		id: string,
		descripcion: string,
		fases: Fase[],
	};
}
export interface Fase {
	xfaseruta_id: string;
	xdescripcion: string;
	xcentro_id: string;
}
export interface InfoComponente {
	idComponente: string;
	referencia: string;
	descComponente: string;
	cantidadPorArticulo: number;
	faseUsado: number | null;
}
export interface OPE {
	idOPE: number,
	fechaCreacion: number;
}
export interface Proveedor {
	idProveedor: string,
	nombre: string;
}
export type InfoArticulo = {
	exists: true,

	id: string,
	descripcion: string,
	referencia: string,
	peso: number,
	controlaLotes: boolean,

	nombreFamilia: string,
	nombreSubFamilia: string,
} | {
	exists: false,
};

export const zInputsCrearOF = z.object({
	cantidadOrden: z.number().min(1, { message: "La cantidad debe ser mayor que 0" }).nullable(),

	inputPrioridad: z.number().min(1).max(5).nullable(),	// Optional porque no se muestra en OPE

	inputLote: z.string().nullable(),			// Optional porque no se muestra en OF
	selectorProveedor: z.string().nullable(),	// Optional porque no se muestra en OF
	selectorOPE: z.string().nullable(),			// Optional porque no se muestra en OF
	textoOF: z.string().max(255),				// Optional porque no se muestra en OPE
});
export type iInputsCrearOF = z.infer<typeof zInputsCrearOF>;


export const zInputsAddComponente = z.object({
	referencia: z.string(),					// Optional porque no se muestra en OF
	cantidadPorUnidad: z.number().min(Number.EPSILON, { message: "La cantidad debe ser mayor que 0" }).nullable(),
});
export type iInputsAddComponente = z.infer<typeof zInputsAddComponente>;
